/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Button, Box, Typography, TextField, MenuItem, CircularProgress, Stack, Skeleton
} from '@mui/material';
import axiosInstance from '../../api/axiosConfig';
import NoResultsImage from '../../assets/no-results.png'; // Add your no-results image
import PageContainer from '../common/PageContainer';
import AdminPageHero from '../common/AdminPageHero';
import AdminPanelCard from '../common/AdminPanelCard';

const HomeworkTableSkeleton = () => (
  <AdminPanelCard
    title="Homework List"
    subtitle="Loading homework assignments for the selected class and date."
    contentSx={{ p: 0 }}
  >
    <TableContainer component={Box}>
      <Table>
        <TableHead sx={{ bgcolor: '#F5F7FB' }}>
          <TableRow>
            {[...Array(6)].map((_, index) => (
              <TableCell key={index} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                <Skeleton width="80%" />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {[...Array(4)].map((_, rowIndex) => (
            <TableRow key={rowIndex}>
              {[...Array(6)].map((_, cellIndex) => (
                <TableCell key={cellIndex} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Skeleton />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </AdminPanelCard>
);

const HomeworkFilter = () => {
  const [homeworks, setHomeworks] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().substring(0, 10)); // Default to current date
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false); // To manage loading state
  const schoolId = localStorage.getItem('schoolId');

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await axiosInstance.get('/api/classes', {
          params: { schoolId },
        });
        setClasses(response.data);
      } catch (error) {
        console.error('Error fetching classes:', error);
      }
    };

    const fetchSections = async () => {
      try {
        const response = await axiosInstance.get('/api/sections', {
          params: { schoolId },
        });
        setSections(response.data);
      } catch (error) {
        console.error('Error fetching sections:', error);
      }
    };

    fetchClasses();
    fetchSections();
  }, []);

  const fetchHomeworks = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/api/homeworks', {
        params: {
          assignedDate: date,
          classId,
          sectionId,
          schoolId,
        },
      });
      setHomeworks(response.data);
    } catch (error) {
      console.error('Error fetching homeworks:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHomeworks(); // Fetch homeworks on component mount
  }, []);

  const handleFilterChange = () => {
    fetchHomeworks();
  };

  const metrics = [
    { label: 'Assignments', value: homeworks.length },
    { label: 'Date', value: date || 'Today' },
    { label: 'Filtered', value: classId || sectionId ? 'Yes' : 'No' },
  ];

  return (
    <PageContainer sx={{ width: '100%' }}>
      <AdminPageHero
        title="Homework"
        subtitle="Review assigned homework by date, class, and section with a cleaner daily view."
        metrics={metrics}
      />

      <AdminPanelCard
        title="Homework Filters"
        subtitle="Choose the date and class filters, then load the matching assignments."
        sx={{ mt: 2 }}
      >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 0.5 }}>
        <TextField
          label="Date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <TextField
          label="Class"
          value={classId}
          onChange={(e) => setClassId(e.target.value)}
          select
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All Classes</MenuItem>
          {classes.map((cls) => (
            <MenuItem key={cls.classId} value={cls.classId}>
              {cls.className}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Section"
          value={sectionId}
          onChange={(e) => setSectionId(e.target.value)}
          select
          sx={{ minWidth: 180 }}
          disabled={!classId} // Disable if no class is selected
        >
          <MenuItem value="">All Sections</MenuItem>
          {sections
            .map((sec) => (
              <MenuItem key={sec.sectionId} value={sec.sectionId}>
                {sec.sectionName}
              </MenuItem>
            ))}
        </TextField>
        <Button variant="contained" onClick={handleFilterChange}>
          Get Homework
        </Button>
      </Stack>
      </AdminPanelCard>

      {/* Loading State */}
      {loading ? (
        <Box sx={{ mt: 2 }}>
          <HomeworkTableSkeleton />
        </Box>
      ) : homeworks.length === 0 ? (
        // No Results Image and Message
        <AdminPanelCard sx={{ mt: 2 }}>
        <Box textAlign="center" mt={1} mb={1}>
          <img src={NoResultsImage} alt="No Results" style={{ width: '200px', opacity: 0.7 }} />
          <Typography variant="h6" color="textSecondary" mt={2}>
            No homework found for the selected criteria
          </Typography>
        </Box>
        </AdminPanelCard>
      ) : (
        <AdminPanelCard
          title="Homework List"
          subtitle="Track title, description, due date, and class assignment in one place."
          sx={{ mt: 2 }}
          contentSx={{ p: 0 }}
        >
        <TableContainer component={Box}>
          <Table>
            <TableHead sx={{ bgcolor: '#F5F7FB' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', color: 'black' }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'black' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'black' }}>Assigned Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'black' }}>Due Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'black' }}>Class</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'black' }}>Section</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {homeworks.map((homework) => (
                <TableRow key={homework.homeworkId} hover sx={{ '& td': { borderBottom: '1px solid', borderColor: 'divider' }, '&:last-child td': { borderBottom: 0 } }}>
                  <TableCell>{homework.title}</TableCell>
                  <TableCell>{homework.description}</TableCell>
                  <TableCell>{homework.assignedDate}</TableCell>
                  <TableCell>{homework.dueDate}</TableCell>
                  <TableCell>{homework.className}</TableCell>
                  <TableCell>{homework.sectionName}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        </AdminPanelCard>
      )}
    </PageContainer>
  );
};

export default HomeworkFilter;
